import { Alert, Grid } from '@mui/material';
import { formatters } from '@utils/formattersV2';
import { HeroBreakdown } from './HeroBreakdown';
import { faDollar } from '@fortawesome/pro-solid-svg-icons';
import { clampNegativeToZero } from '@utils/valueTransformers';
import {
  ChannelGroupingKey,
  Lci3Report,
} from '@components/Dashboard/dashboardSlice/schemas/lci3Reports';
import { IncrementalSalesDonutChart } from './IncrementalSalesDonutChart';
import { TotalProjectedSalesChart } from './TotalProjectedSalesChart';
import { IncrementalSalesLiftSplineChart } from './IncrementalSalesLiftSplineChart';
import { IncrementalSalesSplineChart } from './IncrementalSalesSplineChart';
import { UsersSplineChart } from './UsersSplineChart';
import { ImpressionsSplineChart } from './ImpressionsSplineChart';
import { AIInsightsCard } from '@components/shared/AIInsightsCard';
import { useLciAIInsights } from '@features/ai-insights';

export const channelNames: Record<ChannelGroupingKey, string> = {
  total: 'Overall',
  inStore: 'In-Store',
  online: 'E-Commerce',
} as const;

type SalesMetricsProps = {
  reports: Lci3Report[];
  highlightsComponent: React.ReactNode;
};

export const SalesMetrics = (props: SalesMetricsProps) => {
  const { reports } = props;
  const reportsWithSalesMetrics = reports.filter(report => report.salesMetrics);
  const latestSalesMetrics = reportsWithSalesMetrics.at(-1)?.salesMetrics;
  const latestReport = reportsWithSalesMetrics.at(-1);
  const { rsaAiInsights } = useLciAIInsights(
    latestReport ?? null,
    'key-metrics',
    null
  );

  if (!latestSalesMetrics) return <Alert severity="error">No data</Alert>;

  return (
    <Grid container spacing="1rem">
      <Grid mt="1rem" size={12}>
        <AIInsightsCard
          testId="lci-sales-metrics-ai-insights"
          title="Sales Metrics"
          aiInsights={rsaAiInsights ?? undefined}
          highlights={props.highlightsComponent}
        />
      </Grid>
      <Grid
        size={{
          lg: 4,
          xs: 12,
        }}
      >
        <HeroBreakdown
          title="Incremental Sales Lift"
          colorScheme="sales"
          valueFormatter={formatters.lift}
          icon={faDollar}
          overallMetric={{
            value: clampNegativeToZero(
              latestSalesMetrics.incrementalSalesLift.total ?? 0
            ),
            confidenceLevel: latestSalesMetrics.confidenceLevel.total ?? null,
            category: channelNames.total,
          }}
          subMetrics={[
            {
              value: clampNegativeToZero(
                latestSalesMetrics.incrementalSalesLift.inStore ?? 0
              ),
              confidenceLevel: latestSalesMetrics.confidenceLevel.inStore,
              category: channelNames.inStore,
            },
            {
              value: clampNegativeToZero(
                latestSalesMetrics.incrementalSalesLift.online ?? 0
              ),
              confidenceLevel: latestSalesMetrics.confidenceLevel.online,
              category: channelNames.online,
            },
          ]}
          data-testid="hero-incremental-sales-lift"
        />
      </Grid>
      <Grid
        size={{
          lg: 4,
          xs: 12,
        }}
      >
        <IncrementalSalesDonutChart salesMetrics={latestSalesMetrics} />
      </Grid>
      <Grid
        size={{
          lg: 4,
          xs: 12,
        }}
      >
        <TotalProjectedSalesChart salesMetrics={latestSalesMetrics} />
      </Grid>
      <Grid
        size={{
          lg: 6,
          xs: 12,
        }}
      >
        <IncrementalSalesLiftSplineChart
          reports={reportsWithSalesMetrics}
          salesMetrics={latestSalesMetrics}
        />
      </Grid>
      <Grid
        size={{
          lg: 6,
          xs: 12,
        }}
      >
        <IncrementalSalesSplineChart
          reports={reportsWithSalesMetrics}
          salesMetrics={latestSalesMetrics}
        />
      </Grid>
      <Grid
        size={{
          lg: 6,
          xs: 12,
        }}
      >
        <ImpressionsSplineChart reports={reportsWithSalesMetrics} />
      </Grid>
      <Grid
        size={{
          lg: 6,
          xs: 12,
        }}
      >
        <UsersSplineChart reports={reportsWithSalesMetrics} />
      </Grid>
    </Grid>
  );
};
